// Creature markers, one system everywhere: a grade letter (C B A S S+), a
// crest for Bloodline II-V, a gold tag for auras, a star for favorites and a
// purple diamond for a rare trait. Exact numbers live in the hover card and
// on the creature page, not in chips.
import { CONFIG } from '../shared/config.ts';
import { traitOf } from '../shared/traits.ts';
import { ratingOf } from '../shared/rating.ts';
import { gradeOf, type Grade } from '../shared/grade.ts';
import type { CreatureDTO } from '../shared/protocol.ts';
import { relicGrade, type Relic } from '../shared/items.ts';
import { S } from './state.ts';

const GRADE_CLASS: Record<Grade, string> = { C: 'c', B: 'b', A: 'a', S: 's', 'S+': 'sp' };
const SHIELD = '<svg viewBox="0 0 11 13" aria-hidden="true"><path d="M5.5 .5 10.5 2v4.2c0 3-2.1 5.2-5 6.3C2.6 11.4.5 9.2.5 6.2V2z" fill="currentColor"/></svg>';

/** Grade letter for a 0-100 score. size: '' (20 px), 'sm' (16 px) or 'lg' (40 px). */
export function gradeBadge(score: number, size: '' | 'sm' | 'lg' = '') {
  const g = gradeOf(score);
  return `<span class="gr ${GRADE_CLASS[g]}${size ? ` ${size}` : ''}" aria-label="Grade ${g}">${g}</span>`;
}

export function creatureGrade(c: CreatureDTO, size: '' | 'sm' | 'lg' = '') { return gradeBadge(ratingOf(c.rolls, c.species), size); }

/** A relic's grade letter (shared/items.ts relicGrade), or '' for a relic made before sides. */
export function relicGradeBadge(r: Relic, size: '' | 'sm' | 'lg' = '') {
  const g = relicGrade(r);
  return g ? `<span class="gr ${GRADE_CLASS[g]}${size ? ` ${size}` : ''}" aria-label="Grade ${g}">${g}</span>` : '';
}

/** Bloodline crest, II to V only; Bloodline I shows nothing. `bl` is the 0-based roll. */
export function crest(bl: number) {
  if (bl < 1) return '';
  const name = CONFIG.bloodline[bl].name;
  return `<span class="crest b${bl + 1}" aria-label="Bloodline ${name}">${SHIELD}${name}</span>`;
}

export const auraTag = () => '<span class="aura-tag">✦ Aura</span>';
export const auraDot = () => '<span class="aura-dot" aria-label="Aura">✦</span>';
export const favStar = () => '<span class="fav" aria-label="Favorite">★</span>';
export const rareMark = () => '<span class="rare" aria-label="Rare trait">◆</span>';

export const isFavorite = (c: CreatureDTO) => (S.trainer?.collection ?? []).includes(c.id);
export const hasRareTrait = (c: CreatureDTO) => !!traitOf(c)?.rare;

/** The markers beside a name: crest or aura tag (team rail, log, market rows). */
export function inlineMarks(c: CreatureDTO) {
  return `${c.rolls.aura ? auraTag() : ''}${crest(c.rolls.bloodline)}`;
}

/** Flags stacked in a card's top-right corner: favorite, aura, crest, rare trait. */
export function cardFlags(c: CreatureDTO) {
  return `${isFavorite(c) ? favStar() : ''}${c.rolls.aura ? auraDot() : ''}${crest(c.rolls.bloodline)}${hasRareTrait(c) ? rareMark() : ''}`;
}
